import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { Badge } from '../../../components/ui/Badge';
import {
  useCollectionRules,
  useCreateCollectionRule,
  useUpdateCollectionRule,
  useExecuteCollectionRules,
} from '../../../hooks/useCredit';
import type { CollectionRule, CollectionActionType, CreateCollectionRuleInput } from '../../../types/credit';

const actionLabels: Record<CollectionActionType, string> = {
  SMS: 'SMS',
  EMAIL: 'E-mail',
  PUSH: 'Push',
  PHONE_CALL: 'Ligação',
  LETTER: 'Carta',
  NEGATIVATION: 'Negativação',
  LEGAL_ACTION: 'Ação Judicial',
  SEND_REMINDER: 'Lembrete',
  SEND_WARNING: 'Aviso',
  FLAG_FOR_CALL: 'Marcar Ligação',
  NEGATIVATE: 'Negativar',
  SEND_TO_COLLECTION: 'Cobrança',
};

const actionOptions = Object.entries(actionLabels).map(([value, label]) => ({
  value,
  label,
}));

const initialFormData: CreateCollectionRuleInput = {
  name: '',
  daysOverdue: 1,
  actionType: 'EMAIL',
  template: '',
  isActive: true,
  priority: 1,
};

export function CollectionRules() {
  const [showModal, setShowModal] = useState(false);
  const [editingRule, setEditingRule] = useState<CollectionRule | null>(null);
  const [formData, setFormData] = useState<CreateCollectionRuleInput>(initialFormData);

  const { data: rules, isLoading } = useCollectionRules();
  const createRule = useCreateCollectionRule();
  const updateRule = useUpdateCollectionRule();
  const executeRules = useExecuteCollectionRules();

  const handleOpenModal = (rule?: CollectionRule) => {
    if (rule) {
      setEditingRule(rule);
      setFormData({
        name: rule.name,
        daysOverdue: rule.daysOverdue,
        actionType: rule.actionType,
        template: rule.template || '',
        isActive: rule.isActive,
        priority: rule.priority,
      });
    } else {
      setEditingRule(null);
      setFormData(initialFormData);
    }
    setShowModal(true);
  };

  const handleSubmit = async () => {
    try {
      if (editingRule) {
        await updateRule.mutateAsync({ id: editingRule.id, data: formData });
      } else {
        await createRule.mutateAsync(formData);
      }
      setShowModal(false);
      setEditingRule(null);
      setFormData(initialFormData);
    } catch (error) {
      console.error('Erro ao salvar regra:', error);
    }
  };

  const handleExecute = async () => {
    if (confirm('Executar régua de cobrança agora? Isso enviará notificações para clientes em atraso.')) {
      try {
        await executeRules.mutateAsync();
        alert('Régua de cobrança executada com sucesso!');
      } catch (error) {
        console.error('Erro ao executar régua:', error);
      }
    }
  };

  const sortedRules = rules?.slice().sort((a, b) => a.daysOverdue - b.daysOverdue) || [];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Régua de Cobrança</h1>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={handleExecute} disabled={executeRules.isPending}>
            {executeRules.isPending ? 'Executando...' : 'Executar Agora'}
          </Button>
          <Button onClick={() => handleOpenModal()}>Nova Regra</Button>
        </div>
      </div>

      {/* Timeline Visual */}
      <Card>
        <CardHeader>
          <CardTitle>Linha do Tempo de Cobrança</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8">Carregando...</div>
          ) : !sortedRules.length ? (
            <div className="text-center py-8 text-gray-500">
              Nenhuma regra configurada. Crie sua primeira regra de cobrança.
            </div>
          ) : (
            <div className="relative">
              {/* Linha horizontal */}
              <div className="absolute top-8 left-0 right-0 h-1 bg-gray-200" />
              
              <div className="flex justify-between relative">
                {sortedRules.map((rule, index) => (
                  <div
                    key={rule.id}
                    className="flex flex-col items-center"
                    style={{ width: `${100 / sortedRules.length}%` }}
                  >
                    {/* Marcador */}
                    <div
                      className={`w-4 h-4 rounded-full z-10 ${
                        rule.isActive ? 'bg-blue-500' : 'bg-gray-400'
                      }`}
                    />
                    
                    {/* Card da regra */}
                    <div
                      className={`mt-4 p-3 rounded-lg border text-center cursor-pointer hover:shadow-md transition-shadow ${
                        rule.isActive ? 'bg-white' : 'bg-gray-100'
                      }`}
                      onClick={() => handleOpenModal(rule)}
                    >
                      <p className="font-bold text-lg">{rule.daysOverdue}d</p>
                      <Badge variant={rule.isActive ? 'primary' : 'default'}>
                        {actionLabels[rule.actionType]}
                      </Badge>
                      <p className="text-xs text-gray-500 mt-1">{rule.name}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Lista de Regras */}
      <Card>
        <CardHeader>
          <CardTitle>Todas as Regras</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-2">Nome</th>
                  <th className="text-center py-3 px-2">Dias Atraso</th>
                  <th className="text-center py-3 px-2">Ação</th>
                  <th className="text-center py-3 px-2">Prioridade</th>
                  <th className="text-center py-3 px-2">Status</th>
                  <th className="text-center py-3 px-2">Ações</th>
                </tr>
              </thead>
              <tbody>
                {sortedRules.map((rule) => (
                  <tr key={rule.id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-2">{rule.name}</td>
                    <td className="text-center py-3 px-2">{rule.daysOverdue}</td>
                    <td className="text-center py-3 px-2">
                      <Badge variant="primary">{actionLabels[rule.actionType]}</Badge>
                    </td>
                    <td className="text-center py-3 px-2">{rule.priority}</td>
                    <td className="text-center py-3 px-2">
                      <Badge variant={rule.isActive ? 'success' : 'default'}>
                        {rule.isActive ? 'Ativa' : 'Inativa'}
                      </Badge>
                    </td>
                    <td className="text-center py-3 px-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => handleOpenModal(rule)}
                      >
                        Editar
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Modal de Criação/Edição */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>
                {editingRule ? 'Editar Regra' : 'Nova Regra de Cobrança'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Input
                label="Nome da Regra"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Lembrete 3 dias"
              />

              <Input
                label="Dias de Atraso"
                type="number"
                min={1}
                value={formData.daysOverdue}
                onChange={(e) =>
                  setFormData({ ...formData, daysOverdue: Number(e.target.value) })
                }
              />

              <Select
                label="Tipo de Ação"
                value={formData.actionType}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    actionType: e.target.value as CollectionActionType,
                  })
                }
                options={actionOptions}
              />

              <Input
                label="Prioridade"
                type="number"
                min={1}
                max={10}
                value={formData.priority}
                onChange={(e) =>
                  setFormData({ ...formData, priority: Number(e.target.value) })
                }
              />

              <div>
                <label className="block text-sm font-medium mb-2">
                  Template da Mensagem
                </label>
                <textarea
                  className="w-full p-2 border rounded-lg"
                  rows={4}
                  value={formData.template}
                  onChange={(e) =>
                    setFormData({ ...formData, template: e.target.value })
                  }
                  placeholder="Olá {nome}, sua parcela de {valor} está em atraso há {dias} dias..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Variáveis: {'{nome}'}, {'{valor}'}, {'{dias}'}, {'{vencimento}'}
                </p>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) =>
                    setFormData({ ...formData, isActive: e.target.checked })
                  }
                />
                <label htmlFor="isActive">Regra ativa</label>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  className="flex-1"
                  onClick={() => {
                    setShowModal(false);
                    setEditingRule(null);
                    setFormData(initialFormData);
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  className="flex-1"
                  onClick={handleSubmit}
                  disabled={createRule.isPending || updateRule.isPending}
                >
                  {createRule.isPending || updateRule.isPending
                    ? 'Salvando...'
                    : 'Salvar'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

export default CollectionRules;
